import type { HTMLAttributes } from 'react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { EmptyState } from '../../base/EmptyState';
import { LinkItem, type LinkItemData } from '../LinkItem';
import { LinkItemSkeleton } from '../LinkItemSkeleton';
import styles from './LinkList.module.css';

const sortByDate = (links: LinkItemData[]): LinkItemData[] =>
  [...links].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

interface LinkListProps extends HTMLAttributes<HTMLDivElement> {
  links: LinkItemData[];
  isLoading?: boolean;
  isRefetching?: boolean;
  error?: string | null;
  emptyTitle?: string;
  emptyDescription?: string;
  onLinkClick?: (link: LinkItemData) => void;
  onFavoriteToggle?: (link: LinkItemData) => void;
  onMenuClick?: (link: LinkItemData) => void;
}

export const LinkList = ({
  links,
  isLoading = false,
  isRefetching = false,
  error = null,
  emptyTitle = 'No links yet',
  emptyDescription = 'Save your first link to get started',
  onLinkClick,
  onFavoriteToggle,
  onMenuClick,
  className = '',
  ...props
}: LinkListProps) => {
  const { t } = useTranslation();
  const classNames = [styles.container, className].filter(Boolean).join(' ');
  const sortedLinks = useMemo(() => sortByDate(links), [links]);

  // Loading state - show skeleton items
  if (isLoading) {
    return (
      <div className={classNames} {...props}>
        {Array.from({ length: 5 }).map((_, i) => (
          <LinkItemSkeleton key={i} />
        ))}
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className={classNames} {...props}>
        <EmptyState
          title={t('links.somethingWentWrong')}
          description={error}
          icon="⚠️"
        />
      </div>
    );
  }

  // Empty state
  if (links.length === 0) {
    return (
      <div className={classNames} {...props}>
        <EmptyState
          title={emptyTitle}
          description={emptyDescription}
          icon="📥"
        />
      </div>
    );
  }

  // Flat list (no date group labels)
  return (
    <div className={classNames} {...props}>
      {isRefetching && sortedLinks.length > 0 && <LinkItemSkeleton />}
      {sortedLinks.map((link) => (
        <LinkItem
          key={link.id}
          link={link}
          onClick={onLinkClick}
          onFavoriteToggle={onFavoriteToggle}
          onMenuClick={onMenuClick}
        />
      ))}
    </div>
  );
};
